import { useState, useEffect } from "react";
import FileUpload from "../FileUpload";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import Skeleton from "react-loading-skeleton";
import axios from "axios";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

interface Comment {
  title: string;
  description: string;
  imgUrl: string;
  datePublished: string;
  username: string;
  userPicture: string;
}

interface ActivityPost {
  title: string;
  description: string;
  imgUrl: string;
  datePublished: string;
  username: string;
  userPicture: string;
}

interface selectedProps {
  selected: boolean;
  isPoster: boolean;
  envolvement: {
    activityPost?: ActivityPost[];
  };
}

export default function Activity(props: selectedProps) {
  const { id } = useParams();
  const { getAccessTokenSilently, user } = useAuth0();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [newPost, setNewPost] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await axios.get(
          `${backend_domain}${backend_port}/post/get-comments/${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        setComments(response.data.comments || []);
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [getAccessTokenSilently, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(
        `${backend_domain}${backend_port}/post/add-comment/${id}`,
        {
          title,
          description,
          imgUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Refresh comments after successful post
      const data = response.data;
      setComments([...comments, data]);
      setTitle("");
      setDescription("");
      setImgUrl("");
      setNewPost(false);
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  if (!props.selected) return null;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton height={100} />
        <Skeleton height={100} />
        <Skeleton height={100} />
      </div>
    );
  }

  const renderPost = (post: ActivityPost | Comment) => (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
          {post.userPicture ? (
            <img
              src={post.userPicture}
              alt={post.username}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-gray-400 text-sm">
              {post.username.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{post.username}</p>
              <p className="text-sm text-gray-500">
                {format(new Date(post.datePublished), "MMM d, yyyy")}
              </p>
            </div>
          </div>
          <h3 className="text-lg font-semibold mt-2">{post.title}</h3>
          <p className="text-gray-700 mt-1">{post.description}</p>
          {post.imgUrl && (
            <img
              src={post.imgUrl}
              alt="Post attachment"
              className="mt-3 rounded-lg max-w-full h-auto"
            />
          )}
        </div>
      </div>
    </div>
  );

  const hasActivityPosts =
    props.envolvement?.activityPost &&
    props.envolvement.activityPost.length > 0;
  const hasComments = comments.length > 0;

  return (
    <div className="space-y-6">
      {/* Post Update Button - Only visible to the poster */}
      {props.isPoster && !newPost && (
        <div className="flex justify-end">
          <button
            onClick={() => setNewPost(true)}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>Post Update</span>
          </button>
        </div>
      )}

      {/* Activity Posts and Comments List */}
      {hasActivityPosts || hasComments ? (
        <div className="space-y-4">
          {hasActivityPosts &&
            props.envolvement?.activityPost?.map((post, index) => (
              <div key={`activity-${index}`}>{renderPost(post)}</div>
            ))}
          {hasComments &&
            comments.map((comment, index) => (
              <div key={`comment-${index}`}>{renderPost(comment)}</div>
            ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Updates Yet
          </h3>
          {props.isPoster ? (
            <p className="text-gray-500">
              Be the first to post an update about this envolvement.
            </p>
          ) : (
            <p className="text-gray-500">
              No updates have been posted yet for this envolvement.
            </p>
          )}
        </div>
      )}

      {/* New Post Form */}
      {newPost && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="space-y-4">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700"
              >
                Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <FileUpload chooseImgUrl={setImgUrl} />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setNewPost(false);
                  setTitle("");
                  setDescription("");
                  setImgUrl("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
              >
                Post Update
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
